import {
  AllPointOfInterestsForDataLoaderDocument,
  PointOfInterestsDocument,
  useBulkUpdateReviewActionMutation,
} from '@/__generated__/graphql';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as state from '@/components/Analysis/state';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useMappedFilters } from '@/hooks/useMappedFilters';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { upperSnakeCase } from '@/utils';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useRouter } from '@/hooks';

export const useBulkReviewActionData = ({
  poiType,
  templateId: templateIdProp,
}: {
  poiType?: string;
  templateId?: string;
}) => {
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const structureId = useGetStructureId();
  const allPointOfInterestTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const selectedPoiIds = useAtomValue(state.selectedReviewPointsOfInterest);
  const selectedReviewPoiFilters = useAtomValue(state.selectedReviewPoiFilters);
  const selectedReviewPaintRegionIds = useAtomValue(state.selectedReviewPaintRegionIds);
  const selectedReviewWorkpackIds = useAtomValue(state.selectedReviewWorkpackIds);
  const reviewPoiSearchTerm = useAtomValue(state.reviewPoiSearchTerm);
  const selectedReviewPoiTypeId = useAtomValue(state.selectedReviewPoiTypeId);
  const selectedReviewPoiTypes = useAtomValue(state.selectedReviewPoiTypes);
  const customFilterQueryVarsState = useAtomValue(state.customFilterQueryVarsState);
  const poiFilteredByCheckbox = useAtomValue(poiState.poiFilteredByCheckbox);
  const isAllReviewPointsOfInterestSelected = useAtomValue(
    state.isAllReviewPointsOfInterestSelected
  );
  const appliedAssemblyFilters = useAtomValue(state.appliedAssemblyFilters);
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);

  const router = useRouter();
  const isAtViewerPage = useMemo(() => router.pathname.includes('viewer'), [router.pathname]);

  const [bulkUpdateReviewActionMutation, { loading }] = useBulkUpdateReviewActionMutation();

  const allActions = useMemo(() => {
    if (!allPointOfInterestTemplates) return [];
    return [
      ...new Set(
        allPointOfInterestTemplates.reduce((acc, template) => {
          if (template.fields) {
            const actionField = template.fields.find((field) => field.key === 'review_action');
            if (actionField) {
              acc.push(...actionField.options);
            }
          }
          return acc;
        }, [] as string[])
      ),
    ];
  }, [allPointOfInterestTemplates]);

  const isCurrentPoiTypeSelected = useMemo(() => {
    return selectedReviewPoiTypes.includes(poiType || '');
  }, [selectedReviewPoiTypes, poiType]);

  const templateId = useMemo(() => {
    if (templateIdProp) return templateIdProp;
    if (!poiType && !selectedReviewPoiTypeId) return undefined;
    if (poiType) {
      return allPointOfInterestTemplates.find((template) => {
        const templateKey = upperSnakeCase(template?.key ?? '');
        const templateName = upperSnakeCase(template?.name ?? '');
        const searchType = upperSnakeCase(poiType ?? '');
        return templateKey === searchType || templateName === searchType;
      })?.id;
    }
    return selectedReviewPoiTypeId;
  }, [allPointOfInterestTemplates, poiType, selectedReviewPoiTypeId, templateIdProp]);

  const selectedTemplateKey = useMemo(() => {
    if (!templateId) return undefined;
    return allPointOfInterestTemplates.find((template) => template.id === templateId)?.key;
  }, [allPointOfInterestTemplates, templateId]);

  const poiIds = useMemo(() => {
    if (isAtViewerPage) {
      return poiFilteredByCheckbox
        .filter((poi) => poi.templateId === templateId)
        .map(({ id }) => id);
    }
    if (isAllReviewPointsOfInterestSelected || isCurrentPoiTypeSelected) {
      return [];
    }
    return selectedPoiIds;
  }, [
    isAtViewerPage,
    isAllReviewPointsOfInterestSelected,
    isCurrentPoiTypeSelected,
    selectedPoiIds,
    poiFilteredByCheckbox,
    templateId,
  ]);
  const {
    selectedPoiFilters,
    isGoverningFilters,
    isVerifiedFilters,
    accessibilityFilters,
    statusFilters,
    isManuallyCreatedFilters,
    unAssignedPoiFilters,
    mediaAttachmentFilters,
    deckFilters,
  } = useMappedFilters(selectedReviewPoiFilters, selectedTemplateKey ?? '');

  const updateReviewAction = async (action: string) => {
    try {
      const result = await bulkUpdateReviewActionMutation({
        variables: {
          input: {
            structureId,
            action,
            baseUrl: window.location.origin,
            ...(poiIds.length > 0 && { pointOfInterestIds: poiIds }),
            ...(poiIds.length === 0 && {
              componentIds: selectedAssemblyId ? [selectedAssemblyId] : [],
              selectedTemplateFilters: appliedAssemblyFilters.templateFilters,
              selectedDeckIds: appliedAssemblyFilters.deckFilters,
              paintRegionIds: selectedReviewPaintRegionIds,
              workpackIds: selectedReviewWorkpackIds,
              searchTerm: reviewPoiSearchTerm,
              ...(templateId && { templateId }),
              selectedPoiFilters,
              isGoverning: isGoverningFilters,
              isVerified: isVerifiedFilters,
              accessibility: accessibilityFilters,
              status: statusFilters,
              isManuallyCreatedFilters,
              unAssignedPoiFilters,
              mediaAttachmentFilters,
              deckFilters,
              ...customFilterQueryVarsState,
            }),
          },
        },
        refetchQueries: [PointOfInterestsDocument, AllPointOfInterestsForDataLoaderDocument],
      });
      const updatedPoiCount = result.data?.bulkUpdateReviewAction?.count ?? 0;
      showSnackBar(
        `Review action updated successfully for ${updatedPoiCount} point${
          updatedPoiCount === 1 ? '' : 's'
        } of interest`,
        'success'
      );
    } catch {
      showSnackBar('Error updating review action', 'error');
    }
  };

  const disabled = useMemo(() => {
    if (isAtViewerPage && templateId) {
      return (
        poiFilteredByCheckbox.filter((poi) => poi.templateId === templateId).length === 0 &&
        !isCurrentPoiTypeSelected
      );
    }
    return (
      selectedPoiIds.length === 0 &&
      !isAllReviewPointsOfInterestSelected &&
      !isCurrentPoiTypeSelected
    );
  }, [
    isAtViewerPage,
    templateId,
    selectedPoiIds.length,
    isAllReviewPointsOfInterestSelected,
    isCurrentPoiTypeSelected,
    poiFilteredByCheckbox,
  ]);

  const handleChange = (action: string) => {
    if (action) {
      updateReviewAction(action);
    }
  };

  return {
    allActions,
    handleChange,
    disabled,
    loading,
  };
};
